import { useEffect, useState, type FormEvent } from "react";
import { KeyRound } from "lucide-react";
import { authApi } from "../../api/authApi";
import { HttpError } from "../../api/http";
import { Modal } from "../ui/Modal";

const MIN = 8;
const MAX = 50;

const ERRORS: Record<string, string> = {
  wrong_password: "The current password is incorrect.",
  password_too_short: `Use at least ${MIN} characters.`,
  password_too_long: `Use at most ${MAX} characters.`,
  password_unchanged: "Choose a password that differs from the current one.",
  current_password_required: "Enter your current password.",
  impersonating: "Not available while signed in as a customer.",
  not_available: "This account cannot change its password here.",
};

/** Account menu → "Change password": current + new + repeat, saved via /api/auth/change-password. */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrent("");
    setNext("");
    setRepeat("");
    setError(null);
    setDone(false);
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!current) return setError(ERRORS.current_password_required);
    if (next.length < MIN) return setError(ERRORS.password_too_short);
    if (next.length > MAX) return setError(ERRORS.password_too_long);
    if (next !== repeat) return setError("The new passwords don't match.");
    setBusy(true);
    try {
      await authApi.changePassword(current, next);
      setDone(true);
    } catch (err) {
      const code = err instanceof HttpError ? err.message : "";
      setError(ERRORS[code] ?? (err instanceof Error ? err.message : "Could not change the password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change password"
      busy={busy}
      footer={
        done ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" form="change-password-form" className="btn btn-primary" disabled={busy}>
              <KeyRound size={16} /> {busy ? "Saving…" : "Change password"}
            </button>
          </>
        )
      }
    >
      {done ? (
        <p className="sub" style={{ margin: 0 }}>
          Your password has been changed. Use it the next time you sign in.
        </p>
      ) : (
        <form id="change-password-form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="field">
            <label htmlFor="cp-current">Current password</label>
            <input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="cp-next">New password</label>
            <input
              id="cp-next"
              type="password"
              autoComplete="new-password"
              minLength={MIN}
              maxLength={MAX}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
            <div className="hint">{MIN} to {MAX} characters.</div>
          </div>
          <div className="field">
            <label htmlFor="cp-repeat">Repeat new password</label>
            <input
              id="cp-repeat"
              type="password"
              autoComplete="new-password"
              minLength={MIN}
              maxLength={MAX}
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              required
            />
          </div>
        </form>
      )}
    </Modal>
  );
}
